import React, { useState } from 'react';
import { ChevronDown, Shuffle, Repeat, Volume2, Heart, Download, Check, Loader2 } from 'lucide-react';
import { Track, PlayerTab } from '../types';
import { tg } from '../services/telegram';
import { userStore } from '../services/userStore';
import { monochromeApi } from '../services/monochromeApi';
import {
  VuMeterIcon,
  LyricsIcon,
  QueueRackIcon,
  RewindIcon,
  PlayWedgeIcon,
  PauseBarsIcon,
  FastForwardIcon,
} from './icons/HardwareIcons';
import { SoundcloudWaveform, WaveformComment } from './SoundcloudWaveform';


interface FullPlayerProps {
  isOpen: boolean;
  activeTab: PlayerTab;
  onTabChange: (tab: PlayerTab) => void;
  onClose: () => void;
  track: Track | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  isShuffle: boolean;
  isRepeat: boolean;
  queue: Track[];
  onTogglePlay: () => void;
  onSeek: (seconds: number) => void;
  onNext: () => void;
  onPrev: () => void;
  onSetVolume: (vol: number) => void;
  onToggleShuffle: () => void;
  onToggleRepeat: () => void;
  onSelectTrack: (track: Track) => void;
  onSendFlacToBot?: (track: Track) => void;
}

export const FullPlayer: React.FC<FullPlayerProps> = ({
  isOpen,
  activeTab,
  onTabChange,
  onClose,
  track,
  isPlaying,
  currentTime,
  duration,
  volume,
  isShuffle,
  isRepeat,
  queue,
  onTogglePlay,
  onSeek,
  onNext,
  onPrev,
  onSetVolume,
  onToggleShuffle,
  onToggleRepeat,
  onSelectTrack,
  onSendFlacToBot,
}) => {
  const [flacState, setFlacState] = useState<'idle' | 'loading' | 'success'>('idle');

  const handleSendFlac = async () => {
    if (!track || flacState === 'loading') return;
    setFlacState('loading');
    tg.haptic('medium');

    try {
      if (onSendFlacToBot) {
        onSendFlacToBot(track);
        setFlacState('success');
        setTimeout(() => setFlacState('idle'), 4000);
      } else {
        const res = await monochromeApi.requestFlacToBot(track.id);
        if (res.ok) {
          tg.haptic('success');
          setFlacState('success');
          setTimeout(() => setFlacState('idle'), 4000);
        } else {
          tg.haptic('error');
          setFlacState('idle');
          tg.showAlert(res.error || 'Не удалось отправить файл в бота');
        }
      }
    } catch {
      setFlacState('idle');
    }
  };

  const formatTime = (sec: number) => {
    if (isNaN(sec)) return '00:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // SoundCloud-style interactive comments & reactions (starts pure and clean)
  const [trackComments, setTrackComments] = React.useState<WaveformComment[]>([]);
  const [lyricsLoading, setLyricsLoading] = useState(false);
  const [fetchedLyrics, setFetchedLyrics] = useState<string[]>([]);

  React.useEffect(() => {
    if (!track) {
      setFetchedLyrics([]);
      return;
    }
    if (track.lyrics && track.lyrics.length > 0) {
      setFetchedLyrics(track.lyrics);
      return;
    }

    let active = true;
    setLyricsLoading(true);
    monochromeApi
      .getLyrics(track.title, track.artist)
      .then((lines) => {
        if (active) {
          setFetchedLyrics(lines);
          setLyricsLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setFetchedLyrics([]);
          setLyricsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [track?.id]);

  const handleAddReaction = (emoji: string) => {
    if (!duration) return;
    const ratio = Math.max(0.02, Math.min(0.98, currentTime / duration));
    const userPhoto = (window as any).Telegram?.WebApp?.initDataUnsafe?.user?.photo_url;
    const userName = (window as any).Telegram?.WebApp?.initDataUnsafe?.user?.first_name || 'Вы';

    const newComment: WaveformComment = {
      id: `reaction-${Date.now()}`,
      timeRatio: ratio,
      emoji,
      avatarUrl: userPhoto,
      author: userName,
    };

    setTrackComments((prev) => [...prev, newComment]);
    tg.haptic('medium');
  };


  return (
    <div className={`full-player-sheet ${isOpen ? 'open' : ''}`}>
      <div className="sheet-handle-bar" onClick={onClose} />

      <div className="sheet-header-nav">
        <button type="button" className="btn-icon" onClick={onClose}>
          <ChevronDown size={22} color="var(--champagne)" />
        </button>

        <div style={{ textAlign: 'center' }}>
          <div className="sheet-kicker">СЕЙЧАС ИГРАЕТ</div>
          <div className="sheet-album-name">{track ? track.album : 'СТУДИЙНАЯ ДЕКА'}</div>
        </div>

        <div style={{ width: '32px' }} />
      </div>

      {/* 3 Studio Console Tabs with Custom Hardware Icons */}
      <div className="sheet-tabs-bar">
        <button
          type="button"
          className={`nav-tab-btn ${activeTab === 'player' ? 'active' : ''}`}
          onClick={() => {
            tg.haptic('light');
            onTabChange('player');
          }}
        >
          <VuMeterIcon size={13} />
          <span>ПЛЕЕР</span>
        </button>

        <button
          type="button"
          className={`nav-tab-btn ${activeTab === 'lyrics' ? 'active' : ''}`}
          onClick={() => {
            tg.haptic('light');
            onTabChange('lyrics');
          }}
        >
          <LyricsIcon size={13} />
          <span>ТЕКСТ ПЕСНИ</span>
        </button>

        <button
          type="button"
          className={`nav-tab-btn ${activeTab === 'queue' ? 'active' : ''}`}
          onClick={() => {
            tg.haptic('light');
            onTabChange('queue');
          }}
        >
          <QueueRackIcon size={13} />
          <span>ОЧЕРЕДЬ ({queue.length})</span>
        </button>
      </div>

      {/* TAB 1: MASTER REEL CONSOLE */}
      {activeTab === 'player' && (
        <div className="sheet-tab-body">
          {/* Large 3D Gold Studio Master Reel Showcase */}
          <div className="master-reel-showcase">
            <img
              src="/assets/deck/tape-pack-3d.png"
              alt=""
              className="showcase-tape-pack"
              draggable={false}
            />
            {/* Unified 3D Spinning Rotor: 24K Flange underneath, Album Cover on TOP */}
            <div className={`showcase-rotor ${isPlaying ? 'spinning' : ''}`}>
              <img
                src="/assets/deck/gold-reel-3d.png"
                alt="24K Gold Studio Flange"
                className="showcase-gold-flange"
                draggable={false}
              />
              <div className="showcase-cover-wrap">
                {track?.coverUrl ? (
                  <img
                    src={track.coverUrl}
                    alt={track.title}
                    className="showcase-cover-disc"
                    draggable={false}
                  />
                ) : (
                  <div className="showcase-cover-disc showcase-cover-placeholder">
                    <VuMeterIcon size={36} color="var(--amber)" />
                  </div>
                )}
                <div className="showcase-spindle-hub" />
              </div>
            </div>
          </div>

          <div className="sheet-track-header">
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 className="sheet-track-title">{track?.title || 'ТРЕК НЕ ВЫБРАН'}</h2>
                {track && (
                  <button
                    type="button"
                    className={`btn-like-round ${userStore.isFavorite(track.id) ? 'liked' : ''}`}
                    onClick={() => {
                      userStore.toggleFavorite(track);
                      tg.haptic('light');
                    }}
                    aria-label={userStore.isFavorite(track.id) ? 'Удалить из избранного' : 'Добавить в избранное'}
                  >
                    <Heart
                      size={17}
                      fill={userStore.isFavorite(track.id) ? 'var(--amber)' : 'none'}
                      color={userStore.isFavorite(track.id) ? 'var(--amber)' : 'var(--champagne-dim)'}
                    />
                  </button>
                )}
              </div>
              <div className="sheet-track-artist">
                {track ? track.artist : 'Выберите мастер-ленту в деке или поиске'}
              </div>
            </div>
            <div className="sheet-spec-pill">
              <div>
                {track ? `${track.format} ${track.bitDepth.replace('-bit', ' БИТ')}` : 'STANDBY'}
              </div>
              <div style={{ color: 'var(--champagne-dim)', fontSize: '9px' }}>
                {track ? track.sampleRate.replace('kHz', 'кГц') : '-- кГц'}
              </div>
            </div>
          </div>

          {/* Quick Hardware Action Bar: FLAC в бота */}
          {track && (
            <div className="sheet-flac-action-row">
              <button
                type="button"
                className={`btn-send-flac-bot ${flacState === 'loading' ? 'loading' : ''} ${flacState === 'success' ? 'success' : ''}`}
                onClick={handleSendFlac}
                disabled={flacState === 'loading'}
                title="Отправить оригинальный FLAC-аудиофайл в личный чат Telegram"
              >
                {flacState === 'loading' ? (
                  <Loader2 size={13} className="spinning" />
                ) : flacState === 'success' ? (
                  <Check size={13} color="var(--emerald)" />
                ) : (
                  <Download size={13} />
                )}
                <span className="btn-flac-bot-text">
                  {flacState === 'loading'
                    ? 'ОТПРАВЛЯЮ В TELEGRAM...'
                    : flacState === 'success'
                    ? 'FLAC ОТПРАВЛЕН В ВАШ ЧАТ'
                    : 'ПОЛУЧИТЬ FLAC В БОТА'}
                </span>
                <span className="flac-badge-pill">
                  {track.bitDepth.replace('-bit', ' БИТ')}
                </span>
              </button>
            </div>
          )}

          {/* SoundCloud-Style Waveform with Swipe Seeking & Centered Time Badge */}
          <div className="soundcloud-waveform-card">
            <SoundcloudWaveform
              currentTime={currentTime}
              duration={duration}
              onSeek={onSeek}
              trackId={track?.id || 'master_tape'}
              trackTitle={track?.title || ''}
              height={66}
              barWidth={2.6}
              barGap={1.8}
              showBadge={true}
              comments={trackComments}
            />

            {/* Quick Reactions Bar (as seen on SoundCloud screenshot) */}
            <div className="sc-reaction-bar">
              <span
                className="sc-comment-input-fake"
                onClick={() => handleAddReaction('💬')}
              >
                Оставить реакцию на {formatTime(currentTime)}...
              </span>
              <div className="sc-emoji-buttons-row">
                <button
                  type="button"
                  className="sc-emoji-btn"
                  onClick={() => handleAddReaction('🔥')}
                  title="Огонь"
                >
                  🔥
                </button>
                <button
                  type="button"
                  className="sc-emoji-btn"
                  onClick={() => handleAddReaction('👏')}
                  title="Аплодисменты"
                >
                  👏
                </button>
                <button
                  type="button"
                  className="sc-emoji-btn"
                  onClick={() => handleAddReaction('🥰')}
                  title="В сердечко"
                >
                  🥰
                </button>
                <button
                  type="button"
                  className="sc-emoji-btn"
                  onClick={() => handleAddReaction('✊')}
                  title="Респект"
                >
                  ✊
                </button>
              </div>
            </div>
          </div>


          {/* Hardware Transport Row */}
          <div className="sheet-transport-row">
            <button
              type="button"
              className="btn-icon"
              onClick={() => {
                tg.haptic('light');
                onToggleShuffle();
              }}
              style={{ color: isShuffle ? 'var(--amber)' : 'var(--champagne-dim)' }}
            >
              <Shuffle size={18} />
            </button>

            <button
              type="button"
              className="mech-key"
              style={{ width: '54px', height: '42px' }}
              onClick={() => {
                tg.haptic('light');
                onPrev();
              }}
            >
              <RewindIcon size={16} />
            </button>

            <button
              type="button"
              className={`mech-key play-master ${isPlaying ? 'locked' : ''}`}
              style={{ width: '76px', height: '48px' }}
              onClick={() => {
                tg.haptic('medium');
                onTogglePlay();
              }}
            >
              {isPlaying ? <PauseBarsIcon size={18} /> : <PlayWedgeIcon size={18} />}
              <span style={{ fontSize: '8px' }}>{isPlaying ? 'ПАУЗА' : 'ПУСК'}</span>
            </button>

            <button
              type="button"
              className="mech-key"
              style={{ width: '54px', height: '42px' }}
              onClick={() => {
                tg.haptic('light');
                onNext();
              }}
            >
              <FastForwardIcon size={16} />
            </button>

            <button
              type="button"
              className="btn-icon"
              onClick={() => {
                tg.haptic('light');
                onToggleRepeat();
              }}
              style={{ color: isRepeat ? 'var(--amber)' : 'var(--champagne-dim)' }}
            >
              <Repeat size={18} />
            </button>
          </div>

          {/* Analog Output Gain Fader */}
          <div className="sheet-volume-row">
            <Volume2 size={15} color="var(--champagne)" />
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={(e) => onSetVolume(parseFloat(e.target.value))}
              className="gold-range-slider"
            />
            <span className="sheet-vol-db">
              {Math.round(volume * 100)}%
            </span>
          </div>
        </div>
      )}

      {/* TAB 2: STUDIO LINER NOTES & LYRICS */}
      {activeTab === 'lyrics' && (
        <div className="sheet-scroll-area">
          <div className="liner-notes-card">
            <div className="liner-notes-header">
              <span>{track ? `${track.artist} — ${track.title}` : 'Лайнер-ноты'}</span>
              <span>{track?.bitrate || ''}</span>
            </div>
            {lyricsLoading ? (
              <div style={{ padding: '30px 10px', textAlign: 'center' }}>
                <Loader2 size={20} className="spinning" color="var(--amber)" style={{ margin: '0 auto 8px' }} />
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  Загрузка текста из студийной базы LRCLIB...
                </div>
              </div>
            ) : fetchedLyrics.length > 0 ? (
              fetchedLyrics.map((line, idx) => (
                <p
                  key={idx}
                  className="liner-lyric-line"
                >
                  {line}
                </p>
              ))
            ) : (
              <p className="liner-lyric-line">
                {track
                  ? 'Для этого трека текст пока не найден или это инструментальная композиция.'
                  : 'Выберите трек для просмотра текста.'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: MASTER ARCHIVE QUEUE */}
      {activeTab === 'queue' && (
        <div className="sheet-scroll-area">
          {queue.length === 0 ? (
            <div className="profile-empty-card" style={{ marginTop: '20px' }}>
              <div className="empty-icon-wrap">
                <QueueRackIcon size={28} color="var(--amber)" />
              </div>
              <div className="empty-title">Очередь воспроизведения пуста</div>
              <div className="empty-desc">
                Здесь сохраняются только те треки, которые вы воспроизводили в этой сессии.
              </div>
            </div>
          ) : (
            <div className="cassette-spine-list">
              {queue.map((t, idx) => {
                const isCurr = track ? t.id === track.id : false;
                return (
                  <div
                    key={`q-${t.id}-${idx}`}
                    onClick={() => {
                      tg.haptic('medium');
                      onSelectTrack(t);
                    }}
                    className={`cassette-spine-item ${isCurr ? 'active' : ''}`}
                  >
                    <div className="spine-left">
                      <span className="spine-code">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <img
                        src={t.coverUrl}
                        alt=""
                        className="spine-thumb"
                        loading="lazy"
                      />
                      <div className="spine-meta-wrap">
                        <div className="spine-title">{t.title}</div>
                        <div className="spine-sub">
                          {t.artist} • {t.album}
                        </div>
                      </div>
                    </div>
                    <div className="spine-right">
                      <span className="spine-duration">{t.durationFormatted}</span>
                      <span className="spine-spec">
                        {t.bitDepth === '24-bit' ? '24-бит' : 'FLAC'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
